import type { LegalAidEvidenceModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useDownloadLegalAid,
  useLegalAid,
  useLegalAidPdf,
  useReclassifyReviewThread,
} from "@/hooks/cle-hooks";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useToaster } from "rsuite";

import {
  getReclassifyLocks,
  getMissingEvidenceFields,
} from "../person/id/Efteruddannelse/utils/approvalCompleteness";
import {
  DocumentClassificationActions,
  REPORTED_EVIDENCE_LOCKED_REASON,
} from "../person/id/Efteruddannelse/DocumentClassificationActions";
import { EvidencePdfViewer } from "../person/id/Efteruddannelse/EvidencePdfViewer";
import { StatusBadge } from "../person/id/Efteruddannelse/StatusBadge";
import type { CourseCertificateDecision } from "../person/id/Efteruddannelse/types";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "../person/id/Efteruddannelse/utils/evidenceStatus";
import { formatDate } from "../person/id/Efteruddannelse/utils/formatters";
import { useObjectUrl } from "../person/id/Efteruddannelse/useObjectUrl";
import {
  DocumentAuditTrailPanel,
  DocumentCommentsPanel,
} from "./DocumentReviewPanels";
import { EvidenceReviewThreadTopPanel } from "./EvidenceReviewThreadTopPanel";
import { LegalAidInformation } from "./LegalAidInformation";
import { ReviewDetailLayout } from "./ReviewDetailLayout";
import { ReviewInfoCard, ReviewInfoField } from "./ReviewInfoCard";

interface LegalAidReviewViewProps {
  userId: string;
  legalAidId: string;
  onBack: () => void;
}

export const LegalAidReviewView = ({
  userId,
  legalAidId,
  onBack,
}: LegalAidReviewViewProps) => {
  const { data, isLoading, error } = useLegalAid({ userId, legalAidId });
  const { data: pdfBlob, isLoading: isPdfLoading } = useLegalAidPdf({
    userId,
    legalAidId,
  });
  const toaster = useToaster();
  const { mutate: downloadLegalAid, isPending: isDownloading } =
    useDownloadLegalAid();
  const { mutate: reclassifyLegalAid, isPending: isReclassifying } =
    useReclassifyReviewThread();
  const pdfUrl = useObjectUrl(pdfBlob);
  const [legalAidOverride, setLegalAidOverride] = useState<
    LegalAidEvidenceModel | undefined
  >(undefined);

  useEffect(() => {
    setLegalAidOverride(undefined);
  }, [legalAidId]);

  const legalAid = useMemo(
    () => mergeEvidenceDetailWithOverride(data, legalAidOverride),
    [data, legalAidOverride],
  );

  const handleLegalAidUpdated = (updated: LegalAidEvidenceModel) => {
    setLegalAidOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  const handleReclassify = (decision: CourseCertificateDecision) => {
    reclassifyLegalAid(
      {
        subjectId: legalAidId,
        userId,
        newStatus: decision,
      },
      {
        onSuccess: () => setLegalAidOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={legalAid?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente advokatvagt/retshjælp."
          />
        ) : null
      }
      pdfPanel={
        legalAid ? (
          <EvidencePdfViewer
            fileName={legalAid.fileName ?? "Uploadet dokument"}
            isDownloading={isDownloading}
            isLoading={isPdfLoading}
            onDownload={() => downloadLegalAid({ userId, legalAidId })}
            pdfUrl={pdfUrl}
          />
        ) : null
      }
      auditPanel={<DocumentAuditTrailPanel subjectId={legalAidId} />}
      infoPanel={
        legalAid ? (
          <>
            <ReviewInfoCard title="Advokatvagt/retshjælp-dokument">
              <ReviewInfoField
                label="Filnavn"
                value={legalAid.fileName ?? "-"}
              />
              <ReviewInfoField
                label="Status"
                value={
                  <StatusBadge
                    pill
                    status={formatEvidenceStatus(legalAid.status)}
                    variant={getEvidenceStatusVariant(legalAid.status)}
                  />
                }
              />
              <ReviewInfoField
                label="Indberettet"
                value={legalAid.isReported ? "Ja" : "Nej"}
              />
              <ReviewInfoField
                label="Oprettet"
                value={formatDate(legalAid.createdAt)}
              />
              <ReviewInfoField
                label="Oprettet af"
                value={legalAid.createdByUserName ?? "-"}
              />
            </ReviewInfoCard>
            <LegalAidInformation
              legalAid={legalAid}
              onLegalAidUpdated={handleLegalAidUpdated}
            />
          </>
        ) : null
      }
      actionsPanel={
        legalAid ? (
          <DocumentClassificationActions
            isPending={isReclassifying}
            onReclassify={handleReclassify}
            {...getReclassifyLocks({
              isReported: legalAid.isReported,
              missingFields: getMissingEvidenceFields(legalAid),
              reportedReason: REPORTED_EVIDENCE_LOCKED_REASON,
            })}
          />
        ) : null
      }
      commentsPanel={
        <DocumentCommentsPanel
          subjectId={legalAidId}
          ownerUserId={userId}
          onCommentAdded={() => setLegalAidOverride(undefined)}
          isReported={legalAid?.isReported ?? false}
        />
      }
    />
  );
};
